import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

interface UserContextValue {
  hitsCount: number;
  isHitsReady: boolean;
  /** True only when the user doc exists AND carries a real `hits` value
   *  (numeric or the "Unlimited" string). Lets consumers avoid treating an
   *  unseeded/missing doc's `?? 0` fallback as a genuine zero balance. */
  hitsKnown: boolean;
  hasPendingStripePayment: boolean;
  pendingStripePaymentKind: 'topup' | 'subscription' | null;
  subscriptionName: string | null;
  isPaidPlan: boolean;
  /** True when the account is part of an org (enterprise) — billed via org
   *  quota, NOT the user-doc `hits`. Consumers must NOT gate such users on
   *  `hitsCount`. */
  isEnterprise: boolean;
  /** Firestore `active` flag — whether the (paid) subscription is currently
   *  active. Used to distinguish "out of credits" (active) from
   *  "subscription inactive/expired". */
  subscriptionActive: boolean;
  /** Razorpay subscriptions can self-heal stale inactive Firestore state on
   *  the next backend billing check. Proactive UI gates should let one real
   *  request through for these accounts instead of trapping them locally. */
  razorpaySelfHealCandidate: boolean;
}

const UserContext = createContext<UserContextValue>({
  hitsCount: 0,
  isHitsReady: false,
  hitsKnown: false,
  hasPendingStripePayment: false,
  pendingStripePaymentKind: null,
  subscriptionName: null,
  isPaidPlan: false,
  isEnterprise: false,
  subscriptionActive: false,
  razorpaySelfHealCandidate: false,
});

const isPaidSubscriptionName = (value: unknown) => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase().replace(/\s+/g, '_');
  return Boolean(normalized && normalized !== 'free' && normalized !== 'free_plan');
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uid, setUid] = useState<string | null>(null);
  const [hitsCount, setHitsCount] = useState(0);
  const [isHitsReady, setIsHitsReady] = useState(false);
  const [hitsKnown, setHitsKnown] = useState(false);
  const [hasPendingStripePayment, setHasPendingStripePayment] = useState(false);
  const [pendingStripePaymentKind, setPendingStripePaymentKind] = useState<'topup' | 'subscription' | null>(null);
  const [subscriptionName, setSubscriptionName] = useState<string | null>(null);
  const [isPaidPlan, setIsPaidPlan] = useState(false);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [razorpaySelfHealCandidate, setRazorpaySelfHealCandidate] = useState(false);

  // Track auth state to get uid
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null);
      if (!user) {
        setHitsCount(0);
        setIsHitsReady(false);
        setHitsKnown(false);
        setHasPendingStripePayment(false);
        setPendingStripePaymentKind(null);
        setSubscriptionName(null);
        setIsPaidPlan(false);
        setIsEnterprise(false);
        setSubscriptionActive(false);
        setRazorpaySelfHealCandidate(false);
      }
    });
    return () => unsub();
  }, []);

  // Single Firestore listener for hits
  useEffect(() => {
    if (!uid) return;
    setIsHitsReady(false);
    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        const stripeSubscriptionStatus = data?.stripe_subscription_status || data?.active_status;
        const hasProcessingTopup = data?.last_topup_status === 'processing';
        const hasProcessingSubscription =
          data?.payment_gateway === 'stripe' &&
          Boolean(data?.pending_stripe_subscription_id) &&
          stripeSubscriptionStatus === 'incomplete';
        const nextSubscriptionName =
          typeof data?.subscription_name === 'string'
            ? data.subscription_name
            : typeof data?.plan === 'string'
              ? data.plan
              : null;
        const nextIsPaidPlan = isPaidSubscriptionName(nextSubscriptionName);
        const nextSubscriptionActive = Boolean(data?.active);
        const paymentGateway =
          typeof data?.payment_gateway === 'string'
            ? data.payment_gateway.trim().toLowerCase()
            : '';
        const hasSubscriptionId = Boolean(data?.subscription_id || data?.razorpay_subscription_id);

        const rawHits = data?.hits;
        setHitsCount(rawHits ?? 0);
        setHitsKnown(typeof rawHits === 'number' || typeof rawHits === 'string');
        setHasPendingStripePayment(Boolean(hasProcessingTopup || hasProcessingSubscription));
        setPendingStripePaymentKind(
          hasProcessingTopup ? 'topup' : hasProcessingSubscription ? 'subscription' : null
        );
        setSubscriptionName(nextSubscriptionName);
        setIsPaidPlan(nextIsPaidPlan);
        setIsEnterprise(Boolean(data?.org_id));
        setSubscriptionActive(nextSubscriptionActive);
        setRazorpaySelfHealCandidate(
          nextIsPaidPlan &&
          !nextSubscriptionActive &&
          paymentGateway === 'razorpay' &&
          hasSubscriptionId
        );
        setIsHitsReady(true);
      },
      () => {
        setHitsCount(0);
        setHitsKnown(false);
        setHasPendingStripePayment(false);
        setPendingStripePaymentKind(null);
        setSubscriptionName(null);
        setIsPaidPlan(false);
        setIsEnterprise(false);
        setSubscriptionActive(false);
        setRazorpaySelfHealCandidate(false);
        setIsHitsReady(true);
      }
    );
    return () => unsub();
  }, [uid]);

  return (
    <UserContext.Provider
      value={{
        hitsCount,
        isHitsReady,
        hitsKnown,
        hasPendingStripePayment,
        pendingStripePaymentKind,
        subscriptionName,
        isPaidPlan,
        isEnterprise,
        subscriptionActive,
        razorpaySelfHealCandidate,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserHits = () => useContext(UserContext);
